<template>
  <div>
    <v-card flat class="mb-4">
      <v-card-text class="pb-2 px-0">
        <v-row>
          <v-col v-if="canFilterBySalesmanAndCompany" md="4">
            <company-select
              v-model="searchParams.company_id"
              :item-value="(val) => val.id"
              :itemText="(val) => val.short_name"
              multiple
              chips
              solo
              flat
              :dense="false"
              @change="select(1)"
              background-color="grey lighten-4"
            />
          </v-col>
          <v-col v-if="canFilterBySalesmanAndCompany" md="4">
            <SalesmanSearch
              solo
              flat
              clearable
              :dense="false"
              @input="setSalesman($event)"
              background-color="grey lighten-4"
            />
          </v-col>
          <v-col md="4">
            <CustomerSearch
              solo
              flat
              clearable
              :dense="false"
              @input="setCustomer($event)"
              background-color="grey lighten-4"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col md="4">
            <v-switch
              v-model="searchParams.only_delivered"
              label="Apenas Entregues"
              @change="select(1)"
              color="primary"
              :true-value="1"
              :false-value="0"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-row>
      <v-col cols="12" md="4">
        <v-card
          outlined
          color="grey lighten-4"
          elevation="0"
          class="fill-height"
        >
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Total Geral:
              <br />
              <b class="text-h4">
                {{
                  $format.moneyBr(billReceivableCashOnDeliveryTotals.total || 0)
                }}
              </b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card outlined color="primary" dark elevation="0" class="fill-height">
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Total pago:
              <br />
              <b class="text-h4">
                {{
                  $format.moneyBr(
                    billReceivableCashOnDeliveryTotals.paid_total
                  ) || 0
                }}
              </b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="4">
        <v-card
          outlined
          color="secondary"
          dark
          elevation="0"
          class="fill-height"
        >
          <v-card-text class="d-flex align-center justify-space-between">
            <div>
              Total a Receber:
              <br />
              <b class="text-h4">
                {{
                  $format.moneyBr(
                    billReceivableCashOnDeliveryTotals.total -
                      billReceivableCashOnDeliveryTotals.paid_total
                  ) || 0
                }}
              </b>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="12">
        <v-data-table
          :headers="bill_receivable_headers"
          :items="billReceivableCashOnDelivery.data"
          :items-per-page="-1"
          hide-default-footer
          disable-sort
        >
          <template v-slot:[`item.sale.number`]="{ item }">
            <v-chip color="primary" x-small> #{{ item.sale.number }} </v-chip>
          </template>

          <template v-slot:[`item.customer.name`]="{ item }">
            <span>
              {{ item.customer ? item.customer.name : "Consumidor Final" }}
            </span>
          </template>

          <template v-slot:[`item.due_date`]="{ item }">
            <span>{{ $format.dateBr(item.due_date) }}</span>
          </template>

          <template v-slot:[`item.value`]="{ item }">
            <span class="font-weight-medium">
              {{ $format.moneyBr(item.value) }}
            </span>
          </template>

          <template v-slot:[`item.paid_value`]="{ item }">
            <span class="success--text font-weight-medium">
              {{ $format.moneyBr(item.paid_value || 0) }}
            </span>
          </template>

          <template v-slot:[`item.remainder_value`]="{ item }">
            <span class="error--text font-weight-medium">
              {{ $format.moneyBr(item.remainder_value || item.value) }}
            </span>
          </template>

          <template v-slot:[`item.sale.is_delivered`]="{ item }">
            <v-chip v-if="item.sale.is_delivered" small color="success">
              Concluida
            </v-chip>
            <v-chip v-if="!item.sale.is_delivered" small color="secondary">
              Pendente ({{ item.sale.pending_deliveries_count }})
            </v-chip>
          </template>
          <template v-slot:[`item.status`]="{ item }">
            <v-chip small color="secondary">
              {{ item.status === "to_pay" ? "A Receber" : "-" }}
            </v-chip>
          </template>

          <template v-slot:no-data>
            <div class="text-center pa-4">
              <v-icon large color="grey lighten-2">mdi-cash-remove</v-icon>
              <p class="grey--text mt-2">
                Nenhuma conta a receber encontrada
              </p>
            </div>
          </template>
        </v-data-table>
        <app-pagination
          @click="select($event)"
          :data="billReceivableCashOnDelivery"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import CompanySelect from "@/components/company/ui/CompanySelect.vue";
import SalesmanSearch from "@/components/employees/ui/SalesmanSearch.vue";
import CustomerSearch from "@/components/customer/ui/CustomerSearch.vue";

export default {
  components: {
    CompanySelect,
    SalesmanSearch,
    CustomerSearch,
  },

  data() {
    return {
      searchParams: {
        page: 1,
        company_id: [],
        salesman_id: null,
        customer_id: null,
        only_delivered: 0,
      },

      billReceivableCashOnDelivery: [],
      billReceivableCashOnDeliveryTotals: {},

      isLoading: false,

      bill_receivable_headers: [
        { text: "Venda", value: "sale.number", align: "start", width: "3%" },
        {
          text: "Cliente",
          value: "customer.name",
          align: "start",
          width: "30%",
        },
        { text: "Vencimento", value: "due_date", align: "center" },
        { text: "Valor", value: "value", align: "end", width: "15%" },
        { text: "Valor Pago", value: "paid_value", align: "end", width: "15%" },
        { text: "Saldo", value: "remainder_value", align: "end", width: "15%" },
        {
          text: "Entrega recebida",
          value: "sale.is_delivered",
          align: "end",
          width: "20%",
        },
        { text: "Status", value: "status", align: "center" },
      ],
    };
  },

  mounted() {
    if (!this.canFilterBySalesmanAndCompany) {
      this.searchParams.salesman_id = this.user.id;
    }

    this.select(1);
  },

  computed: {
    canFilterBySalesmanAndCompany() {
      return this.$acl.isManager() || this.$acl.isCashier();
    },

    user() {
      return this.$store.state.auth.userData.user;
    },
  },

  methods: {
    async select(page = 1) {
      this.searchParams.page = page;

      try {
        const response = await this.$http.index(
          "bill-receivable/bill-rec-cash-on-del-report",
          this.searchParams
        );

        this.billReceivableCashOnDelivery = response.bill_receivables;

        this.billReceivableCashOnDeliveryTotals = response.totals;
      } catch (error) {
        console.error("Erro ao carregar vendas por cliente:", error);
      }
    },

    setCustomer(customer) {
      this.searchParams.customer_id = customer ? customer.id : null;
      this.select(1);
    },

    setSalesman(salesman) {
      this.searchParams.salesman_id = salesman ? salesman.id : null;
      this.select(1);
    },
  },
};
</script>
